import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { ChevronDown, ChevronUp, ClockHistory } from 'react-bootstrap-icons'
import DraftAssetPreview from '../../../../../Shared/DraftAssetPreview'

// Tasklist #12: nothing is ever overwritten (every submission is its own
// DraftVersion row, feedback pinned to the version it was given against),
// but until now there was no way to actually browse that history - the
// review screen only ever showed the latest version. Every version already
// arrives fully loaded on the Action (Campaigns::Drafts::ActionDraftBlueprint
// already renders the whole `versions` array), so this is display-only.
export default observer(function VersionHistorySection({ actionDraft }) {
  const [expandedId, setExpandedId] = useState(null)

  // Most recent first - deliberately includes the version currently shown
  // above in the main review panel too, not just strictly-past ones: for an
  // Action sitting in revision_requested (no "current submission" being
  // actively reviewed at all), the most recent submission *is* the one
  // worth looking back at, so excluding it would hide exactly the version
  // a PM opens this panel to see.
  const versions = actionDraft.versions.filter(v => v.isSubmitted).slice().reverse()

  if (versions.length === 0) return null

  return (
    <div className="border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4 w-[650px] mb-4 bg-white dark:bg-rb-dark-mode">
      <p className="font-bold mb-1 dark:text-white">Version History</p>

      {versions.map(version => {
        const isOpen = expandedId === version.id
        const hasFeedback = version.feedback.length > 0 || version.client_feedback.length > 0

        return (
          <div key={version.id} className="border-b-1 border-rb-widget-border dark:border-rb-dark-mode-lighter last:border-none">
            <button
              onClick={() => setExpandedId(isOpen ? null : version.id)}
              className="flex items-center justify-between w-full py-2 text-left"
            >
              <span className="flex items-center gap-2 text-sm dark:text-white">
                <ClockHistory className="text-rb-mat-grey shrink-0" />
                Version {version.version_number} · {new Date(version.submitted_at).toLocaleString()}
                {version.submitted_by && (
                  <span className="text-rb-mat-grey dark:text-rb-dark-mode-text-grey">· {version.submitted_by}</span>
                )}
              </span>
              {isOpen ? <ChevronUp className="shrink-0" /> : <ChevronDown className="shrink-0" />}
            </button>

            {isOpen && (
              <div className="pb-3 pl-6 flex flex-col gap-3">
                {version.draft_assets.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {version.draft_assets.map(asset => (
                      <DraftAssetPreview key={asset.id} asset={asset} className="max-h-40 rounded-lg" />
                    ))}
                  </div>
                )}

                {version.caption && (
                  <div>
                    <p className="text-xs font-bold text-rb-mat-grey dark:text-rb-dark-mode-text-grey mb-1">Caption</p>
                    <p className="text-sm dark:text-white whitespace-pre-line">{version.caption}</p>
                  </div>
                )}

                {version.notes && (
                  <div>
                    <p className="text-xs font-bold text-rb-mat-grey dark:text-rb-dark-mode-text-grey mb-1">Links / Notes</p>
                    <p className="text-sm dark:text-white whitespace-pre-line">{version.notes}</p>
                  </div>
                )}

                {version.feedback.map(f => (
                  <div key={f.id} className="border-1 border-orange-300 bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2">
                    <p className="text-xs font-bold text-orange-700 dark:text-orange-300 mb-1">Feedback to influencer</p>
                    <p className="text-sm dark:text-white whitespace-pre-line">{f.body}</p>
                  </div>
                ))}

                {version.client_feedback.map(f => (
                  <div key={f.id} className="border-1 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2">
                    <p className="text-xs font-bold text-amber-800 dark:text-amber-300 mb-1">Feedback from Client</p>
                    <p className="text-sm dark:text-white whitespace-pre-line">{f.body}</p>
                  </div>
                ))}

                {!hasFeedback && (
                  <p className="text-sm text-rb-mat-grey dark:text-rb-dark-mode-text-grey italic">No feedback was recorded for this version.</p>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
})
